import { useMemo, useState } from 'react'
import { useScheduleData } from '../hooks/useScheduleData.js'
import { formatMoney } from '../lib/format.js'
import PrintScheduleModal from './PrintScheduleModal.jsx'
import RosterAssignmentModal from './RosterAssignmentModal.jsx'
import ScheduleImportModal from './ScheduleImportModal.jsx'
import RosterScanModal from './RosterScanModal.jsx'
import ScheduleSuggestModal from './ScheduleSuggestModal.jsx'
import { Skeleton, SkeletonList } from './Skeleton.jsx'
import StatusPill from './StatusPill.jsx'
import { toneFor, labelFor } from '../lib/pillStatus.js'
import Toast from './Toast.jsx'
import KitchenLoader from './KitchenLoader.jsx'

// Weekly schedule view — Level 2 of staffing.
//
// Layout:
//   1. Week navigation header — prev/next + range label + Today jump
//   2. Week summary tile — total labor + hours + shift count
//   3. Day picker grid (7 cells) — day name + date + per-day labor
//   4. Selected-day section — shifts for the chosen day with edit on tap
//
// On phones the 7-day grid is comfortable because each cell only needs
// ~50px (e.g. "Wed / 29 / $530"). The "actual" 2D scheduler grid
// (rows = staff, cols = days) doesn't fit on a phone — we may add it
// for tablet/desktop later, but the day-selector is the better mobile UX.
export default function ScheduleScreen() {
  // Default to today's week (Monday-anchored).
  const [weekStart,    setWeekStart]    = useState(() => mondayOf(new Date()))
  const [selectedDate, setSelectedDate] = useState(() => isoDate(new Date()))
  const [createOpen,   setCreateOpen]   = useState(false)
  const [editingAssignment, setEditingAssignment] = useState(null)
  const [removingId,   setRemovingId]   = useState(null)
  const [importOpen,   setImportOpen]   = useState(false)
  const [scanOpen,     setScanOpen]     = useState(false)
  const [printOpen,    setPrintOpen]    = useState(false)
  const [suggestOpen,  setSuggestOpen]  = useState(false)
  const [toast,        setToast]        = useState('')
  // Approve-and-execute in-flight state. 'approve' shows the KitchenLoader
  // (an execution state, never the blue spinner); 'skip' just disables.
  const [approveBusy,  setApproveBusy]  = useState(null)

  const {
    byDay,
    weekTotals,
    activeStaff,
    pendingScheduleAction,
    loading,
    refresh,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    approveScheduleFix,
    skipScheduleFix,
  } = useScheduleData(weekStart)

  // The selected-day rollup (shifts + per-day cost). If the user picks
  // a day that's outside the current week (shouldn't happen via UI but
  // be defensive), fall back to first day.
  const selectedDay = byDay.find((d) => d.date === selectedDate) ?? byDay[0] ?? null

  const todayStr = isoDate(new Date())
  const isCurrentWeek = byDay.some((d) => d.date === todayStr)

  // Pretty range label for the header. E.g. "Apr 27 – May 3, 2026"
  const rangeLabel = useMemo(() => {
    if (byDay.length < 7) return ''
    const start = parseDate(byDay[0].date)
    const end   = parseDate(byDay[6].date)
    const fmt = (d, opts) => d.toLocaleDateString('en-US', opts)
    if (start.getMonth() === end.getMonth()) {
      return `${fmt(start, { month: 'short', day: 'numeric' })} – ${fmt(end, { day: 'numeric', year: 'numeric' })}`
    }
    return `${fmt(start, { month: 'short', day: 'numeric' })} – ${fmt(end, { month: 'short', day: 'numeric', year: 'numeric' })}`
  }, [byDay])

  function jumpToToday() {
    const m = mondayOf(new Date())
    setWeekStart(m)
    setSelectedDate(todayStr)
  }
  function prevWeek() {
    const newStart = addDays(weekStart, -7)
    setWeekStart(newStart)
    // Keep the *position* in the week — if Tuesday was selected, jump
    // to Tuesday of the new week, not blindly to its first day.
    const offset = byDay.findIndex((d) => d.date === selectedDate)
    setSelectedDate(addDays(newStart, offset >= 0 ? offset : 0))
  }
  function nextWeek() {
    const newStart = addDays(weekStart, 7)
    setWeekStart(newStart)
    const offset = byDay.findIndex((d) => d.date === selectedDate)
    setSelectedDate(addDays(newStart, offset >= 0 ? offset : 0))
  }

  async function handleCreate(payload) {
    await createAssignment(payload)
    setToast('Shift added')
  }

  async function handleUpdate(id, patch) {
    await updateAssignment(id, patch)
    setToast('Shift updated')
  }

  async function handleDelete(id) {
    await deleteAssignment(id)
    setToast('Shift removed')
  }

  // Imported handler — passed to ScheduleImportModal. Refreshes the
  // current week's data so any imported shifts inside the visible
  // window appear immediately.
  async function handleImported({ inserted, skipped_dupe, skipped_unmatched }) {
    const parts = []
    if (inserted          > 0) parts.push(`${inserted} added`)
    if (skipped_dupe      > 0) parts.push(`${skipped_dupe} duplicate${skipped_dupe === 1 ? '' : 's'} skipped`)
    if (skipped_unmatched > 0) parts.push(`${skipped_unmatched} unmatched skipped`)
    setToast(parts.length > 0 ? parts.join(' \u00b7 ') : 'No new shifts to import.')
    await refresh()
  }

  // Photo scan finished \u2014 staff and/or shifts inserted in the modal.
  // Refresh so any new staff + shifts in the visible window appear.
  async function handleScanned({ staffCreated, shiftsInserted, skippedDupe, skippedUnmatched, skippedNoEnd }) {
    const parts = []
    if (staffCreated     > 0) parts.push(`${staffCreated} staff added`)
    if (shiftsInserted   > 0) parts.push(`${shiftsInserted} ${shiftsInserted === 1 ? 'shift' : 'shifts'} added`)
    if (skippedDupe      > 0) parts.push(`${skippedDupe} duplicate${skippedDupe === 1 ? '' : 's'} skipped`)
    if (skippedUnmatched > 0) parts.push(`${skippedUnmatched} skipped \u2014 no staff match`)
    if (skippedNoEnd     > 0) parts.push(`${skippedNoEnd} skipped \u2014 add an end time`)
    setToast(parts.length > 0 ? parts.join(' \u00b7 ') : 'Nothing new to import.')
    await refresh()
  }

  // AI suggestion accepted — bulk insert finished in the modal. Auto-
  // jump the visible week to the target week so the manager sees what
  // they just scheduled, refresh data, and surface counts in the toast.
  async function handleSuggestAccepted(result) {
    const { count, weekStart: targetWeek, replaced } = result || {}
    if (targetWeek) {
      setWeekStart(targetWeek)
      setSelectedDate(targetWeek)
    }
    await refresh()
    const parts = []
    if (replaced > 0) parts.push(`replaced ${replaced} existing`)
    parts.push(`scheduled ${count}`)
    setToast(`AI ${parts.join(', ')} ${count === 1 ? 'shift' : 'shifts'}.`)
  }

  // Quick-remove from the row (the ✕ button). Skip the modal; just
  // confirm + delete inline. For full edits, tap the row body.
  async function handleQuickRemove(a, e) {
    e?.stopPropagation?.()
    if (removingId) return
    const ok = window.confirm(
      `Remove this shift?\n\n${a.staff?.name ?? 'Unknown'} on ${a.shift_date}`
    )
    if (!ok) return
    setRemovingId(a.id)
    try {
      await deleteAssignment(a.id)
      setToast('Shift removed')
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ScheduleScreen] quick-remove failed', err)
      setToast('Could not remove. Try again.')
    } finally {
      setRemovingId(null)
    }
  }

  // Approve the frozen schedule fix — the agent's one-tap shift cancel.
  // KitchenLoader shows while in flight (execution state). On failure we keep
  // the card mounted so the operator can retry; the fn is idempotent. Submit
  // state resets in finally so the card never locks on the next render.
  async function handleApproveScheduleFix() {
    if (approveBusy || !pendingScheduleAction) return
    const name = pendingScheduleAction.spec?.staff_name ?? 'this staffer'
    const isTrim = pendingScheduleAction.spec?.change_type === 'trim'
    setApproveBusy('approve')
    try {
      await approveScheduleFix(pendingScheduleAction.id)
      setToast(isTrim ? `Shift shortened for ${name}.` : `Shift cancelled for ${name}.`)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleApproveScheduleFix]', e)
      setToast("We couldn't update the schedule. Try again.")
    } finally {
      setApproveBusy(null)
    }
  }

  async function handleSkipScheduleFix() {
    if (approveBusy || !pendingScheduleAction) return
    setApproveBusy('skip')
    try {
      await skipScheduleFix(pendingScheduleAction.id)
      setToast('Schedule change skipped.')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleSkipScheduleFix]', e)
      setToast("We couldn't skip that. Try again.")
    } finally {
      setApproveBusy(null)
    }
  }

  return (
    <div className="min-h-full bg-white pb-12">
      <main className="mx-auto max-w-screen-sm">
        {/* Approve-and-execute card — the morning brief's one-tap shift cancel.
            Shows the frozen spec summary; Approve cancels ONE over-40h shift
            (status flip scheduled -> cancelled), Skip dismisses. Sentence case
            throughout, no emoji, no AI-cost/model language. */}
        {pendingScheduleAction && (
          <section
            className="mx-4 mt-4 rounded-2xl border border-amber-brand-400/40 bg-amber-50 p-5 shadow-sm"
            aria-label="Schedule change awaiting approval"
          >
            <div className="text-xs font-medium text-amber-900/70">
              Ready to apply from this morning's brief
            </div>
            <div className="mt-2 text-base font-medium text-black">
              {pendingScheduleAction.spec?.staff_name ?? 'Staff member'}
            </div>

            {/* What one tap applies: the shift date + times, read verbatim off
                the frozen spec so the card and the applied change agree. A trim
                shows the shortened end (start–end → start–newEnd); a cancel
                shows the single time range. */}
            {(() => {
              const s = pendingScheduleAction.spec ?? {}
              const isTrim = s.change_type === 'trim'
              const start  = String(s.start_time || '').slice(0, 5)
              const end    = String(s.end_time   || '').slice(0, 5)
              const newEnd = String(s.new_end_time || '').slice(0, 5)
              const when   = s.shift_date ? prettyDate(s.shift_date) : 'a shift'
              return (
                <div className="mt-3 rounded-xl border border-amber-brand-400/30 bg-white px-3 py-2.5">
                  <div className="text-sm font-medium text-black">
                    {isTrim ? 'Shorten' : 'Cancel'} {when}
                  </div>
                  <div className="mt-0.5 text-xs text-black/55">
                    {isTrim
                      ? (start && end && newEnd
                          ? `${start}–${end} → ${start}–${newEnd}`
                          : 'time on file')
                      : (start && end ? `${start}–${end}` : 'time on file')}
                  </div>
                </div>
              )
            })()}

            {/* Weekly hours before and after the cancel. */}
            {(() => {
              const s = pendingScheduleAction.spec ?? {}
              const before = Number(s.weekly_hours_before)
              const after  = Number(s.weekly_hours_after)
              if (!Number.isFinite(before) || !Number.isFinite(after)) return null
              return (
                <div className="mt-2 flex items-baseline justify-between text-sm">
                  <span className="text-black/70">Weekly hours</span>
                  <span className="font-medium tabular-nums text-black">
                    {before.toFixed(1)} hrs → {after.toFixed(1)} hrs
                  </span>
                </div>
              )
            })()}

            {approveBusy === 'approve' ? (
              <div className="mt-4">
                <KitchenLoader
                  size="sm"
                  align="row"
                  messages={
                    pendingScheduleAction.spec?.change_type === 'trim'
                      ? ['Updating the schedule…', 'Shortening the shift…', 'Almost there…']
                      : ['Updating the schedule…', 'Cancelling the shift…', 'Almost there…']
                  }
                />
              </div>
            ) : (
              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleApproveScheduleFix}
                  disabled={!!approveBusy}
                  className="min-h-btn flex-1 rounded-lg bg-emerald-600 px-4 py-3 text-base font-medium text-white hover:bg-emerald-700 active:bg-emerald-800 disabled:cursor-wait disabled:opacity-50"
                >
                  {pendingScheduleAction.spec?.change_type === 'trim'
                    ? 'Approve and shorten this shift'
                    : 'Approve and cancel this shift'}
                </button>
                <button
                  type="button"
                  onClick={handleSkipScheduleFix}
                  disabled={!!approveBusy}
                  className="min-h-btn rounded-lg border border-black/15 bg-white px-4 py-3 text-base font-medium text-black/70 hover:bg-black/5 disabled:cursor-wait disabled:opacity-50"
                >
                  Skip
                </button>
              </div>
            )}
            <p className="mt-2 text-xs text-black/55 leading-snug">
              {pendingScheduleAction.spec?.change_type === 'trim'
                ? 'Shortens one over-40h shift to bring the week under 40. The staff schedule updates; no one is messaged.'
                : 'Cancels one over-40h shift. The staff schedule updates; no one is messaged.'}
            </p>
          </section>
        )}

        {/* ---------- 1. Week navigation ----------------------- */}
        <section className="px-4 pt-4">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={prevWeek}
              aria-label="Previous week"
              className="min-h-btn min-w-[44px] rounded-lg px-3 text-lg text-black/70 hover:bg-black/5"
            >
              ←
            </button>
            <div className="flex-1 text-center">
              <div className="text-sm font-medium text-black/85">{rangeLabel}</div>
              {!isCurrentWeek && (
                <button
                  type="button"
                  onClick={jumpToToday}
                  className="text-xs font-medium text-blue-600 hover:underline"
                >
                  Jump to this week
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={nextWeek}
              aria-label="Next week"
              className="min-h-btn min-w-[44px] rounded-lg px-3 text-lg text-black/70 hover:bg-black/5"
            >
              →
            </button>
          </div>

          {/* Secondary actions: AI suggest, import a schedule (CSV/ICS),
              or print. AI Suggest gets the prominent blue style because
              it's the most-used flow once the manager is trained on it. */}
          <div className="mt-2 flex flex-wrap justify-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => setSuggestOpen(true)}
              className="rounded-lg bg-blue-600 px-3 py-1.5 font-medium text-white shadow-sm hover:bg-blue-700"
            >
              AI Suggest schedule
            </button>
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className="rounded-lg border border-black/15 bg-white px-3 py-1.5 font-medium text-black/70 hover:border-blue-600 hover:text-blue-700"
            >
              Import schedule
            </button>
            <button
              type="button"
              onClick={() => setScanOpen(true)}
              className="rounded-lg border border-black/15 bg-white px-3 py-1.5 font-medium text-black/70 hover:border-blue-600 hover:text-blue-700"
            >
              Scan schedule photo
            </button>
            <button
              type="button"
              onClick={() => setPrintOpen(true)}
              className="rounded-lg border border-black/15 bg-white px-3 py-1.5 font-medium text-black/70 hover:border-blue-600 hover:text-blue-700"
            >
              Print / export
            </button>
          </div>
        </section>

        {/* ---------- 2. Week summary tile -------------------- */}
        <section className="mx-4 mt-3 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
          <div className="text-[11px] font-medium text-black/55">
            Week labor
          </div>
          <div className="mt-1 flex items-baseline justify-between gap-3">
            <div className="text-3xl font-medium tabular-nums">
              {formatMoney(weekTotals.cost)}
            </div>
            <div className="text-right text-xs text-black/60">
              <div className="tabular-nums font-medium">
                {weekTotals.hours.toFixed(1)} hrs
              </div>
              <div>
                {weekTotals.shiftCount} {weekTotals.shiftCount === 1 ? 'shift' : 'shifts'}
                {weekTotals.staffCount > 0 && ` \u00b7 ${weekTotals.staffCount} staff`}
              </div>
            </div>
          </div>
        </section>

        {/* ---------- 3. Day picker grid (7 cells) ------------- */}
        <section className="mx-4 mt-3" aria-label="Day selector">
          {loading && byDay.length === 0 ? (
            <div className="grid grid-cols-7 gap-1">
              {[0,1,2,3,4,5,6].map((i) => (
                <div key={i} className="h-[60px] rounded-lg border border-black/10 p-1.5">
                  <Skeleton className="h-2 w-6" />
                  <Skeleton className="mt-1 h-3 w-8" />
                  <Skeleton className="mt-1 h-2 w-10" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {byDay.map((d) => {
                const isSelected = d.date === selectedDate
                const isToday    = d.date === todayStr
                const dt = parseDate(d.date)
                return (
                  <button
                    key={d.date}
                    type="button"
                    onClick={() => setSelectedDate(d.date)}
                    aria-pressed={isSelected}
                    className={`
                      flex flex-col items-center justify-center gap-0.5
                      rounded-lg border px-1 py-2
                      transition
                      ${isSelected
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : isToday
                          ? 'border-amber-brand-200 bg-amber-brand-50/60 text-amber-brand-900'
                          : 'border-black/10 bg-white text-black/75 hover:bg-black/[0.03]'}
                    `}
                  >
                    <div className="text-[10px] font-medium opacity-70">
                      {dt.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className={`text-base font-medium tabular-nums ${isSelected ? '' : ''}`}>
                      {dt.getDate()}
                    </div>
                    <div className={`text-[10px] tabular-nums ${d.cost > 0 ? 'font-medium' : 'opacity-60'}`}>
                      {d.cost > 0 ? formatMoney(d.cost) : '\u2014'}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </section>

        {/* ---------- 4. Selected-day shifts ------------------- */}
        {selectedDay && (
          <section className="mx-4 mt-6" aria-label="Shifts for selected day">
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <div className="min-w-0">
                <h2 className="truncate text-base font-medium">
                  {parseDate(selectedDay.date).toLocaleDateString('en-US', {
                    weekday: 'long', month: 'short', day: 'numeric',
                  })}
                  {selectedDay.date === todayStr && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-amber-brand-50 px-2 py-0.5 text-[11px] font-medium text-amber-brand-800">
                      Today
                    </span>
                  )}
                </h2>
                <div className="text-xs text-black/55">
                  {selectedDay.assignments.length === 0
                    ? 'No shifts scheduled'
                    : `${selectedDay.assignments.length} ${selectedDay.assignments.length === 1 ? 'shift' : 'shifts'} \u00b7 ${formatMoney(selectedDay.cost)} \u00b7 ${selectedDay.hours.toFixed(1)} hrs`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                disabled={activeStaff.length === 0}
                title={activeStaff.length === 0 ? 'Add staff in Settings first' : undefined}
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                + Add shift
              </button>
            </div>

            {loading && selectedDay.assignments.length === 0 ? (
              <SkeletonList rows={3} />
            ) : selectedDay.assignments.length === 0 ? (
              <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
                {activeStaff.length === 0 ? (
                  <>Add staff in <span className="font-medium text-blue-600">Settings → Staff</span> first.</>
                ) : (
                  <>No shifts on this day. Tap <span className="font-medium text-blue-600">+ Add shift</span> to schedule someone.</>
                )}
              </div>
            ) : (
              <ul className="overflow-hidden rounded-xl border border-black/10">
                {selectedDay.assignments.map((a) => {
                  const isRemoving = removingId === a.id
                  const start = (a.start_time || '').slice(0, 5)
                  const end   = (a.end_time   || '').slice(0, 5)
                  const wage  = Number(a.staff?.hourly_wage || 0)
                  const hours = hoursBetween(a.start_time, a.end_time)
                  const counts = a.status === 'scheduled' || a.status === 'completed'
                  const cost  = counts ? hours * wage : 0
                  return (
                    <li
                      key={a.id}
                      className={`flex items-center gap-3 border-b border-black/5 last:border-b-0 ${isRemoving ? 'opacity-50' : ''}`}
                    >
                      <button
                        type="button"
                        onClick={() => setEditingAssignment(a)}
                        className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left hover:bg-blue-50/50 active:bg-blue-50"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-base font-medium">
                            {a.staff?.name ?? '(deleted staff)'}
                            <StatusBadge status={a.status} />
                          </div>
                          <div className="truncate text-xs text-black/60">
                            {start && end ? `${start}\u2013${end}` : 'time TBD'}
                            {' \u00b7 '}
                            {hours > 0 ? `${hours.toFixed(1)} hrs` : '0 hrs'}
                            {a.role ? ` \u00b7 ${a.role}` : (a.staff?.role ? ` \u00b7 ${a.staff.role}` : '')}
                          </div>
                        </div>
                        <div className={`shrink-0 text-right tabular-nums ${counts ? 'font-medium' : 'text-black/40'}`}>
                          {formatMoney(cost)}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleQuickRemove(a, e)}
                        disabled={isRemoving}
                        aria-label={`Remove ${a.staff?.name ?? 'shift'}`}
                        title="Remove this shift"
                        className="min-h-btn min-w-[44px] rounded-lg px-2 mr-2 text-base text-black/40 hover:bg-red-50 hover:text-red-600 active:bg-red-100 disabled:cursor-wait disabled:opacity-50"
                      >
                        {isRemoving ? '\u2026' : '\u2715'}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        )}
      </main>

      {/* Create modal — adds a shift to the currently-selected day */}
      <RosterAssignmentModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        activeStaff={activeStaff}
        shiftDate={selectedDate}
      />

      {/* Edit modal — opens when the manager taps an existing shift */}
      <RosterAssignmentModal
        open={!!editingAssignment}
        onClose={() => setEditingAssignment(null)}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        activeStaff={activeStaff}
        assignment={editingAssignment}
      />

      {/* CSV / ICS import — bulk seeds shifts from existing schedule */}
      <ScheduleImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        activeStaff={activeStaff}
        onImported={handleImported}
      />

      {/* Photo scan — extract staff + shifts from a schedule photo */}
      <RosterScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        activeStaff={activeStaff}
        kind="both"
        onImported={handleScanned}
      />

      {/* Print / export — date range + view toggle + window.print() */}
      <PrintScheduleModal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        initialWeekStart={weekStart}
      />

      {/* AI Schedule Suggestion — Tier 1 of post-Path-B AI features */}
      <ScheduleSuggestModal
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        onAccepted={handleSuggestAccepted}
      />

      <Toast message={toast} onDone={() => setToast('')} />
    </div>
  )
}

// ---------- helpers ----------------------------------------
function StatusBadge({ status }) {
  if (!status || status === 'scheduled') return null
  return (
    <span className="ml-2">
      <StatusPill tone={toneFor('shift', status)}>
        {labelFor('shift', status)}
      </StatusPill>
    </span>
  )
}

// Hours between HH:MM strings, wrapping past midnight.
function hoursBetween(start, end) {
  if (!start || !end) return 0
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  const startMin = sh * 60 + sm
  let endMin     = eh * 60 + em
  if (endMin < startMin) endMin += 24 * 60
  return Math.max(0, (endMin - startMin) / 60)
}

// Date helpers — locale-immune, browser-local.
function isoDate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
function parseDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d, 0, 0, 0, 0)
}
// "Sat, Jun 20" for a YYYY-MM-DD shift date on the approve card.
function prettyDate(dateStr) {
  try {
    return parseDate(dateStr).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    })
  } catch {
    return dateStr
  }
}
function addDays(dateStr, n) {
  const d = parseDate(dateStr)
  d.setDate(d.getDate() + n)
  return isoDate(d)
}
// Returns Monday of the week containing `date`. Restaurant convention:
// Mon = first day of week.
function mondayOf(date) {
  const d = new Date(date)
  const day = d.getDay() // 0 = Sun, 1 = Mon, ...
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  return isoDate(d)
}
